/**
 * NewPlanModal — Formulaire complet "Nouveau plan d'action".
 * Champs : corners (multi/search), responsables (multi/search), natures (checkboxes),
 * priorité (radios visuels), constat, objectif, date fin, actions (répétable), résultat.
 * Boutons : "Créer le plan" (bleu #2563EB) + "Clôturer plan d'action" (statut done).
 * Supporte initialData pour pré-remplissage depuis une alerte.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { X, Target, Search, Check, Plus, Trash2, ChevronDown, Lock } from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const NATURES = ["Ressources humaines","Qualité","Organisation","Client","Commercial","Sécurité","Formation","Financier","Autre"];
const PRIORITIES = [
  { v:"haute",   l:"Critique",  color:"#EF4444" },
  { v:"elevee",  l:"Élevée",    color:"#F59E0B" },
  { v:"moyenne", l:"Moyenne",   color:"#F59E0B" },
  { v:"basse",   l:"Faible",    color:"#16A34A" },
];
const ACTION_STATUSES = [
  { v:"todo",        l:"À faire"    },
  { v:"in_progress", l:"En cours"   },
  { v:"waiting",     l:"En attente" },
  { v:"done",        l:"Terminé"    },
];

/* ─── Composant MultiSelect avec recherche ─────────────────────────── */
function MultiSelectSearch({ options, value, onChange, placeholder, testid, icon }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrap = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    return s ? options.filter(o => (o.label||"").toLowerCase().includes(s)) : options;
  }, [q, options]);
  const selectedItems = useMemo(() => options.filter(o => value.includes(o.id)), [options, value]);

  const toggle = (id) => onChange(value.includes(id) ? value.filter(x=>x!==id) : [...value, id]);

  return (
    <div ref={wrap} className="relative" data-testid={testid}>
      <div role="button" tabIndex={0} onClick={()=>setOpen(o=>!o)}
           onKeyDown={(e)=>{ if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen(o=>!o); } }}
           className="w-full min-h-[42px] px-3 py-2 rounded-lg border border-slate-200 bg-white text-left flex items-center flex-wrap gap-1.5 hover:border-blue-400 transition cursor-pointer focus:outline-none focus:border-blue-400">
        {selectedItems.length === 0 && <span className="text-[13px] text-slate-400">{placeholder}</span>}
        {selectedItems.map(s => (
          <span key={s.id} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-700">
            {icon}{s.label}
            <button type="button" onClick={(e)=>{ e.stopPropagation(); toggle(s.id); }} className="hover:text-blue-900"><X className="w-3 h-3"/></button>
          </span>
        ))}
        <ChevronDown className="w-4 h-4 text-slate-400 ml-auto"/>
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="p-2 border-b border-slate-100 relative">
            <Search className="w-3.5 h-3.5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input autoFocus value={q} onChange={e=>setQ(e.target.value)} placeholder="Rechercher..."
                   className="w-full pl-7 pr-2 py-1.5 rounded border border-slate-200 text-[12px]"/>
          </div>
          <div className="max-h-52 overflow-auto">
            {filtered.length === 0 && <div className="p-3 text-[12px] italic text-slate-400 text-center">Aucun résultat</div>}
            {filtered.slice(0, 200).map(o => {
              const on = value.includes(o.id);
              return (
                <button type="button" key={o.id} onClick={()=>toggle(o.id)}
                        className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center gap-2 hover:bg-slate-50 ${on?"bg-blue-50":""}`}>
                  <div className={`w-4 h-4 rounded border grid place-items-center shrink-0 ${on?"bg-blue-600 border-blue-600 text-white":"border-slate-300 bg-white"}`}>
                    {on && <Check className="w-3 h-3"/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{o.label}</div>
                    {o.sub && <div className="text-[10px] text-slate-500 truncate">{o.sub}</div>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Modal principal ──────────────────────────────────────────────── */
export default function NewPlanModal({ open, onClose, onCreated, stores = [], users = [], initialData = null }) {
  const { user } = useAuth() || {};
  const [f, setF] = useState({
    store_ids: [], responsible_ids: [], natures: [], nature_other: "",
    priority: "moyenne", constat: "", objective: "", deadline: "",
    actions: [], result: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({
      store_ids: initialData?.store_id ? [initialData.store_id] : [],
      responsible_ids: initialData?.responsible_id ? [initialData.responsible_id] : (user?.id ? [user.id] : []),
      natures: initialData?.natures || [],
      nature_other: "",
      priority: initialData?.priority || "moyenne",
      constat: initialData?.description || "",
      objective: initialData?.title || "",
      deadline: initialData?.deadline || "",
      actions: [],
      result: "",
    });
  }, [open, initialData, user]);

  const storeOptions = useMemo(() => stores.map(s => ({
    id: s.id, label: s.canonical_name || s.name || "—", sub: s.enseigne,
  })), [stores]);
  const userOptions = useMemo(() => users
    .filter(u => ["bm","manager","rt","mm","director","admin"].includes(u.role))
    .map(u => ({ id: u.id, label: u.name, sub: u.role?.toUpperCase() })), [users]);

  if (!open) return null;

  const toggleNature = (n) => setF(v => ({...v, natures: v.natures.includes(n) ? v.natures.filter(x=>x!==n) : [...v.natures, n]}));
  const addAction = () => setF(v => ({...v, actions: [...v.actions, { description:"", responsible_id: user?.id || "", deadline:"", status:"todo" }]}));
  const updAction = (i, patch) => setF(v => ({...v, actions: v.actions.map((a,idx)=> idx===i ? {...a, ...patch} : a)}));
  const rmAction = (i) => setF(v => ({...v, actions: v.actions.filter((_,idx)=>idx!==i)}));

  const validate = () => {
    if (f.store_ids.length === 0) { toast.error("Sélectionnez au moins un corner"); return false; }
    if (f.responsible_ids.length === 0) { toast.error("Sélectionnez au moins un responsable"); return false; }
    if (!f.constat.trim() && !f.objective.trim()) { toast.error("Constat ou Objectif obligatoire"); return false; }
    return true;
  };

  const buildPayload = (close = false) => {
    // Enrich actions with responsible name for display
    const actions = f.actions.filter(a => a.description.trim()).map(a => {
      const u = users.find(u => u.id === a.responsible_id);
      return { ...a, responsible: u?.name };
    });
    return {
      store_ids: f.store_ids,
      responsible_ids: f.responsible_ids,
      natures: f.natures,
      nature_other: f.natures.includes("Autre") ? f.nature_other : null,
      priority: f.priority,
      constat: f.constat,
      objective: f.objective,
      deadline: f.deadline || null,
      actions,
      result: f.result,
      close,
      source_action_id: initialData?.source_action_id || undefined,
    };
  };

  const submit = async (close = false) => {
    if (!validate()) return;
    if (close && !f.result.trim()) { toast.error("Le résultat est obligatoire pour clôturer un plan"); return; }
    setSaving(true);
    try {
      await api.post("/ops/plans", buildPayload(close));
      toast.success(close ? "Plan clôturé" : "Plan d'action créé");
      onCreated?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="new-plan-modal">
      <div onClick={e=>e.stopPropagation()} className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl my-4 max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-2 px-5 py-3.5 border-b border-slate-100">
          <div className="w-9 h-9 rounded-lg bg-blue-600 text-white grid place-items-center"><Target className="w-4 h-4"/></div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-bold text-slate-900 leading-none">Nouveau plan d'action</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">Formalisez une action terrain claire, mesurable et suivie.</p>
          </div>
          <button type="button" onClick={onClose} className="w-8 h-8 rounded hover:bg-slate-100 grid place-items-center"><X className="w-4 h-4"/></button>
        </div>

        {/* Body scrollable */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Corner + Responsable */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Corner concerné <span className="text-rose-500">*</span></label>
              <MultiSelectSearch options={storeOptions} value={f.store_ids}
                                 onChange={v=>setF(x=>({...x, store_ids:v}))}
                                 placeholder="Sélectionner un ou plusieurs corners"
                                 testid="np-corner" />
            </div>
            <div>
              <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Responsable(s) <span className="text-rose-500">*</span></label>
              <MultiSelectSearch options={userOptions} value={f.responsible_ids}
                                 onChange={v=>setF(x=>({...x, responsible_ids:v}))}
                                 placeholder="Sélectionner les responsables"
                                 testid="np-resp" />
            </div>
          </div>

          {/* Nature de la problématique */}
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Nature de la problématique</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="np-natures">
              {NATURES.map(n => {
                const on = f.natures.includes(n);
                return (
                  <button type="button" key={n} onClick={()=>toggleNature(n)}
                          className={`flex items-center gap-2 text-left px-3 py-2 rounded-lg border text-[12px] transition ${on?"border-blue-500 bg-blue-50 text-blue-900 font-semibold":"border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
                    <div className={`w-4 h-4 rounded border grid place-items-center shrink-0 ${on?"bg-blue-600 border-blue-600 text-white":"border-slate-300"}`}>
                      {on && <Check className="w-3 h-3"/>}
                    </div>
                    <span className="truncate">{n}</span>
                  </button>
                );
              })}
            </div>
            {f.natures.includes("Autre") && (
              <input value={f.nature_other} onChange={e=>setF(v=>({...v, nature_other:e.target.value}))}
                     placeholder="Précisez la nature..." maxLength={80}
                     className="mt-2 w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]"
                     data-testid="np-nature-other"/>
            )}
          </div>

          {/* Priorité — radios visuels */}
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Priorité</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="np-prio">
              {PRIORITIES.map(p => {
                const on = f.priority === p.v;
                return (
                  <button type="button" key={p.v} onClick={()=>setF(v=>({...v, priority:p.v}))}
                          data-testid={`np-prio-${p.v}`}
                          style={on ? { borderColor: p.color, background: p.color+"15" } : {}}
                          className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-[12px] font-semibold transition ${on?"":"border-slate-200 hover:border-slate-300"}`}>
                    <div className="w-3 h-3 rounded-full ring-2 ring-white" style={{ background: p.color }}/>
                    <span style={on ? { color: p.color } : { color: "#334155" }}>{p.l}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Constat + Objectif */}
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Constat</label>
            <textarea rows={3} value={f.constat} onChange={e=>setF(v=>({...v, constat:e.target.value}))}
                      placeholder="Décrivez la situation observée..."
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] resize-none focus:outline-none focus:border-blue-400"
                      data-testid="np-constat"/>
          </div>
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Objectif</label>
            <input value={f.objective} onChange={e=>setF(v=>({...v, objective:e.target.value}))}
                   placeholder="Ex : Réduire l'absentéisme de 20% avant le 30/09"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:border-blue-400"
                   data-testid="np-objective"/>
          </div>

          {/* Date fin prévue */}
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Date de fin prévue</label>
            <input type="date" value={f.deadline} onChange={e=>setF(v=>({...v, deadline:e.target.value}))}
                   className="w-full sm:w-56 px-3 py-2 rounded-lg border border-slate-200 text-[13px] focus:outline-none focus:border-blue-400"
                   data-testid="np-deadline"/>
          </div>

          {/* Actions répétables */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[11px] font-bold text-slate-700">Actions</label>
              <button type="button" onClick={addAction} data-testid="np-add-action"
                      className="flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:text-blue-700 px-2 py-1 rounded hover:bg-blue-50">
                <Plus className="w-3.5 h-3.5"/> Ajouter une action
              </button>
            </div>
            {f.actions.length === 0 ? (
              <div className="text-center py-4 text-[12px] italic text-slate-400 border border-dashed border-slate-200 rounded-lg">
                Aucune action. Cliquez sur « Ajouter une action » pour commencer.
              </div>
            ) : (
              <div className="space-y-2" data-testid="np-actions-list">
                {f.actions.map((a, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 rounded-lg p-2">
                    <input value={a.description} onChange={e=>updAction(i,{description:e.target.value})}
                           placeholder="Description de l'action" data-testid={`np-action-desc-${i}`}
                           className="col-span-12 sm:col-span-5 px-2 py-1.5 rounded border border-slate-200 text-[12px] bg-white"/>
                    <SearchSelect value={a.responsible_id} onChange={v => updAction(i,{responsible_id:v})} options={[...userOptions.map(u => ({ value: u.id, label: u.label }))]} placeholder={"— Responsable —"} data-testid={`np-action-resp-${i}`} size="lg" menuWidth={260}/>
                    <input type="date" value={a.deadline||""} onChange={e=>updAction(i,{deadline:e.target.value})}
                           data-testid={`np-action-deadline-${i}`}
                           className="col-span-4 sm:col-span-2 px-2 py-1.5 rounded border border-slate-200 text-[11px] bg-white"/>
                    <SearchSelect value={a.status} onChange={v => updAction(i,{status:v})} options={[...ACTION_STATUSES.map(s => ({ value: s.v, label: s.l }))]} data-testid={`np-action-status-${i}`} size="lg" menuWidth={260}/>
                    <button type="button" onClick={()=>rmAction(i)}
                            className="col-span-2 sm:col-span-1 w-8 h-8 mx-auto rounded hover:bg-rose-100 text-slate-400 hover:text-rose-600 grid place-items-center"
                            data-testid={`np-action-rm-${i}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Résultat */}
          <div>
            <label className="text-[11px] font-bold text-slate-700 block mb-1.5">Résultat</label>
            <textarea rows={3} value={f.result} onChange={e=>setF(v=>({...v, result:e.target.value}))}
                      placeholder="Quel résultat obtenu..."
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] resize-none focus:outline-none focus:border-blue-400"
                      data-testid="np-result"/>
            <p className="text-[10px] text-slate-400 mt-1">Requis uniquement pour clôturer le plan.</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-100 bg-slate-50">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-slate-600 hover:bg-white">Annuler</button>
          <div className="flex items-center gap-2">
            <button type="button" onClick={()=>submit(true)} disabled={saving}
                    data-testid="np-close"
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50">
              <Lock className="w-3.5 h-3.5"/> Clôturer plan d'action
            </button>
            <button type="button" onClick={()=>submit(false)} disabled={saving}
                    data-testid="np-submit"
                    style={{ backgroundColor: "#2563EB" }}
                    className="px-5 py-2 rounded-lg text-[13px] font-semibold text-white hover:brightness-110 disabled:opacity-50">
              {saving ? "Enregistrement…" : "Créer le plan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
